import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { useEditorStore } from '../../store/useEditorStore';
import { useTranslation } from 'react-i18next';
import {
  OrbitControls,
  setupLights,
  createFloorMeshes,
  fitCameraToScene,
} from '../../utils/three-helper';

interface Canvas3DProps {
  width: number;
  height: number;
  onLoadComplete?: () => void;
}

const Canvas3D: React.FC<Canvas3DProps> = ({ width, height, onLoadComplete }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const animationIdRef = useRef<number | null>(null);
  const modelGroupRef = useRef<THREE.Group | null>(null);

  const { project, currentFloor, savePreviewCameraState, hiddenCategories } = useEditorStore();

  // 追踪 project 的 updatedAt 以检测深层变化
  // 这确保当元素的 scale 等属性变化时，useEffect 能正确触发
  const projectVersionRef = useRef<string>(project?.updatedAt || '');

  // 监听 updatedAt 变化并触发重新渲染
  useEffect(() => {
    if (project?.updatedAt) {
      console.log('🔔 [Canvas3D] 检测到 project.updatedAt 变化:');
      console.log('   上一个版本:', projectVersionRef.current);
      console.log('   当前版本:', project.updatedAt);
      console.log('   project 存在:', !!project);
      console.log('   当前楼层:', currentFloor);

      // 比较版本号，如果不相同则记录
      if (projectVersionRef.current !== project.updatedAt) {
        console.log('🔄 [Canvas3D] 版本号不同，需要重新渲染3D场景');
        projectVersionRef.current = project.updatedAt;
      }
    }
  }, [project?.updatedAt, currentFloor]);

  // 初始化Three.js场景
  useEffect(() => {
    if (!containerRef.current) return;

    // 创建场景
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5); // 浅灰色背景
    sceneRef.current = scene;

    // 创建相机
    const camera = new THREE.PerspectiveCamera(
      60,
      width / height,
      0.1,
      1000
    );
    camera.position.set(15, 15, 15);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // 创建渲染器
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    console.log('🖼️ 预览渲染器配置:', {
      outputColorSpace: renderer.outputColorSpace,
      shadowMapEnabled: renderer.shadowMap.enabled,
      shadowMapType: renderer.shadowMap.type,
      toneMapping: renderer.toneMapping,
      toneMappingExposure: renderer.toneMappingExposure
    });

    rendererRef.current = renderer;

    containerRef.current.appendChild(renderer.domElement);

    // 创建控制器
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.minDistance = 5;
    controls.maxDistance = 100;
    controls.maxPolarAngle = Math.PI / 2;
    controlsRef.current = controls;

    // 首次加载时不恢复相机状态，让 fitCameraToScene 自动设置
    // 如果需要恢复相机状态，应该在 fitCameraToScene 之后调用

    // 添加光照
    setupLights(scene);

    // 创建模型组
    const modelGroup = new THREE.Group();
    scene.add(modelGroup);
    modelGroupRef.current = modelGroup;

    // 动画循环
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // 清理函数
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      // 清空场景中的所有对象
      if (sceneRef.current) {
        while (sceneRef.current.children.length > 0) {
          sceneRef.current.remove(sceneRef.current.children[0]);
        }
      }
      renderer.dispose();
      controls.dispose();
    };
  }, [width, height]);

  // 更新场景尺寸
  useEffect(() => {
    if (rendererRef.current && cameraRef.current) {
      rendererRef.current.setSize(width, height);
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
    }
  }, [width, height]);

  // 更新3D模型
  useEffect(() => {
    console.log('🔔 [Canvas3D useEffect] ==================== 检测到依赖项变化 ====================');
    console.log('🔔 [Canvas3D useEffect] project 存在:', !!project);
    console.log('🔔 [Canvas3D useEffect] currentFloor:', currentFloor);
    console.log('🔔 [Canvas3D useEffect] project.updatedAt:', project?.updatedAt);
    console.log('🔔 [Canvas3D useEffect] projectVersionRef.current:', projectVersionRef.current);
    console.log('🔔 [Canvas3D useEffect] hiddenCategories:', hiddenCategories);

    if (!modelGroupRef.current || !project || !currentFloor) {
      console.log('🔔 [Canvas3D useEffect] 条件不满足，跳过渲染');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    console.log('🔔 [Canvas3D useEffect] 开始重新渲染3D场景...');

    // 清空现有模型
    while (modelGroupRef.current.children.length > 0) {
      const child = modelGroupRef.current.children[0];
      modelGroupRef.current.remove(child);

      // 释放几何体和材质
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        } else if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        }
      }
    }

    // 查找当前楼层
    const floor = project.floors.find((f) => f.id === currentFloor);
    if (!floor) {
      console.log('🔔 [Canvas3D useEffect] 未找到当前楼层:', currentFloor);
      setIsLoading(false);
      return;
    }

    console.log(`🔔 [Canvas3D useEffect] 找到楼层: ${floor.name}, 元素数量: ${floor.layers.reduce((sum, l) => sum + l.elements.length, 0)}`);

    // 使用requestAnimationFrame延迟渲染，避免阻塞主线程
    const rafId = requestAnimationFrame(async () => {
      try {
        console.log('🎬 [Canvas3D] ==================== 开始生成3D场景 ====================');

        // 创建楼层的3D网格（传递hiddenCategories以过滤隐藏的类别）
        const floorMeshes = createFloorMeshes(floor, hiddenCategories);
        
        if (modelGroupRef.current) {
          modelGroupRef.current.add(floorMeshes);
          console.log(`✅ 楼层网格已添加: ${floorMeshes.children.length} 个元素`);
        }

        // 加载3D家具模型（过滤隐藏的类别）
        const { createAll3DFurnitureMeshes } = await import('../../utils/model3DLoader');
        const model3DElements = floor.layers
          .flatMap(layer => layer.elements)
          .filter(el => {
            if (el.type !== 'model3d') return false;
            // 检查3D模型类别是否被隐藏
            if (hiddenCategories && hiddenCategories['3D模型']) {
              console.log('🚫 3D模型类别已隐藏，跳过渲染');
              return false;
            }
            return true;
          }) as any[];

        console.log(`🪑 找到 ${model3DElements.length} 个3D家具模型（hiddenCategories:`, hiddenCategories, '）');

        // 调试：输出所有3D模型的详细信息
        if (model3DElements.length > 0) {
          console.log('📋 3D模型详情:');
          model3DElements.forEach((model, idx) => {
            const centerX = model.points.reduce((sum: number, p: any) => sum + p.x, 0) / model.points.length;
            const centerY = model.points.reduce((sum: number, p: any) => sum + p.y, 0) / model.points.length;
            console.log(`  [${idx + 1}] ${model.modelName}: canvas位置=(${centerX.toFixed(1)}, ${centerY.toFixed(1)}px), 3D位置=(${(centerX/100).toFixed(2)}, 0, ${(centerY/100).toFixed(2)})m`);
          });
        }

        if (model3DElements.length > 0) {
          console.log(`🪑 [Canvas3D] 开始加载 ${model3DElements.length} 个3D家具模型...`);
          console.log(`🪑 [Canvas3D] 3D家具使用固定层级 3000，确保在所有区域之上`);
          try {
            // 3D家具使用固定层级（3000），始终在所有区域之上
            const furnitureMeshes = await createAll3DFurnitureMeshes(model3DElements);
            if (modelGroupRef.current) {
              modelGroupRef.current.add(furnitureMeshes);
              console.log(`✅ [Canvas3D] 家具模型已添加: ${furnitureMeshes.children.length} 个`);
              // 输出每个模型的详细信息
              furnitureMeshes.children.forEach((child: any, idx: number) => {
                console.log(`   [${idx + 1}] ${child.name}: renderOrder=${child.renderOrder}, Y=${child.position.y.toFixed(6)}`);
              });
            }
          } catch (furnitureError) {
            console.error('❌ [Canvas3D] 家具模型加载失败:', furnitureError);
            // 继续渲染其他元素，不中断整个流程
          }
        } else {
          console.log(`🪑 [Canvas3D] 没有找到3D家具模型`);
        }

        // 渲染文本元素（过滤隐藏的类别）
        const { createTextSprite } = await import('../../utils/three-helper');
        const textElements = floor.layers
          .flatMap(layer => layer.elements)
          .filter(el => {
            if (el.type !== 'text' || !el.visible) return false;
            // 检查文本类别是否被隐藏
            if (hiddenCategories && hiddenCategories['文本']) return false;
            return true;
          }) as any[];

        console.log(`📝 找到 ${textElements.length} 个文本元素`);

        if (textElements.length > 0 && modelGroupRef.current) {
          // 3D场景中文本的缩放比例系数
          // 由于3D场景中1单位=1米，而Canvas中1单位=1像素
          // 需要将字体大小放大才能在3D场景中看清
          const TEXT_SCALE_FACTOR = 2.8; // 文本放大倍数

          textElements.forEach((textEl: any) => {
            if (textEl.points.length === 0) return;

            const [point] = textEl.points;
            const x = point.x / 100; // Canvas pixels to 3D meters
            const z = point.y / 100;
            // 文本elevation单位是mm，需要按照墙面比例缩放
            const LOGICAL_WALL_HEIGHT = 3000; // 逻辑墙高3000mm（3米）
            const RENDER_WALL_HEIGHT = 0.375; // 渲染墙高0.375米
            const elevationMm = textEl.elevation || 8500; // 默认8500mm（850cm）
            const y = (elevationMm / LOGICAL_WALL_HEIGHT) * RENDER_WALL_HEIGHT;

            const sprite = createTextSprite({
              content: textEl.content,
              fontSize: (textEl.fontSize || 16) * TEXT_SCALE_FACTOR,
              color: textEl.color || '#000000',
              fontFamily: textEl.fontFamily || 'sans-serif',
              bold: textEl.bold || false,
              italic: textEl.italic || false,
              align: textEl.align || 'center',
              borderColor: textEl.borderColor || '#ADD8E6',
              backgroundColor: textEl.backgroundColor || '#FFFFFF',
              backgroundOpacity: textEl.backgroundOpacity ?? 0.8,
              padding: textEl.padding || 8,
              position: { x, y, z },
            });

            modelGroupRef.current?.add(sprite);
          });

          console.log(`✅ 文本元素已添加: ${textElements.length} 个`);
        }

        // 如果有元素，自动调整相机
        if (floorMeshes.children.length > 0 && cameraRef.current && controlsRef.current && sceneRef.current) {
          // 延迟一帧以确保几何体已添加
          setTimeout(() => {
            if (cameraRef.current && controlsRef.current && sceneRef.current) {
              console.log('📷 自动调整相机视角...');
              fitCameraToScene(cameraRef.current, controlsRef.current, sceneRef.current);
            }
            setIsLoading(false);
            onLoadComplete?.();
            console.log('✅ 3D场景生成完成');
          }, 100);
        } else {
          setIsLoading(false);
          onLoadComplete?.();
          console.log('✅ 3D场景生成完成（无元素）');
        }
      } catch (error) {
        console.error('❌ 3D模型生成失败:', error);
        setIsLoading(false);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [project, currentFloor, onLoadComplete, hiddenCategories, projectVersionRef.current]);

  // 监听相机变化并保存状态
  useEffect(() => {
    if (!controlsRef.current || !cameraRef.current) return;

    const handleCameraChange = () => {
      if (cameraRef.current && controlsRef.current) {
        savePreviewCameraState({
          position: {
            x: cameraRef.current.position.x,
            y: cameraRef.current.position.y,
            z: cameraRef.current.position.z,
          },
          target: {
            x: controlsRef.current.target.x,
            y: controlsRef.current.target.y,
            z: controlsRef.current.target.z,
          },
          zoom: cameraRef.current.zoom,
        });
      }
    };

    const controls = controlsRef.current;
    controls.addEventListener('change', handleCameraChange);

    return () => {
      controls.removeEventListener('change', handleCameraChange);
    };
  }, [savePreviewCameraState]);

  return (
    <div ref={containerRef} className="w-full h-full relative bg-gray-100">
      {/* 加载状态 */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
            <div className="text-gray-700 text-sm">{t('preview.generating3D')}</div>
          </div>
        </div>
      )}

      {/* 控制提示 */}
      <div className="absolute top-4 left-4 bg-white bg-opacity-90 shadow-md text-gray-800 px-3 py-2 rounded text-xs border border-gray-200">
        <div className="font-medium mb-1">{t('preview.controls')}</div>
        <div className="space-y-0.5 text-gray-600">
          <div>{t('preview.rotate')}</div>
          <div>{t('preview.pan')}</div>
          <div>{t('preview.zoomControl')}</div>
        </div>
      </div>

      {/* 元素统计 */}
      {project && currentFloor && (
        <div className="absolute bottom-4 right-4 bg-white bg-opacity-90 shadow-md text-gray-800 px-3 py-2 rounded text-xs border border-gray-200">
          <div>
            {t('preview.floor')}:{' '}
            {project.floors.find((f) => f.id === currentFloor)?.name || t('preview.unknown')}
          </div>
          <div>
            {t('preview.elements')}: {
              project.floors
                .find((f) => f.id === currentFloor)
                ?.layers.reduce((sum, layer) => sum + layer.elements.length, 0) || 0
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default Canvas3D;
